import { ReactNode, Fragment, Children, useState, useId } from 'react';
import styles from './Carousel.module.scss';

import { Colors } from '../../../theme/Colors';
import { BaseButton } from '../../navigation/LegacyButton/BaseButton';
import { LegacyButtonProps } from '../../navigation/LegacyButton';
import { Icon, IconName } from '../../display/Icon';
import { Indicator } from '../../navigation/Indicator';
import { OnPaginateData, Paginator } from '../../navigation/Paginator';
import { ScrollSnap } from './ScrollSnap';

type ChangeSource = 'scroll' | 'next' | 'previous' | 'paginator' | 'indicator';

type Props = {
  /** The images to render in the Carousel. */
  children: ReactNode;
  /**
   * Width of each slide.
   *
   * **NOTE:** Widths need to match for each child element.
   */
  slideWidth: number;
  /**
   * Margin (px) between slides
   *
   * @deprecated gap now enforced at 20px.
   */
  gap?: number;
  /**
   * Show the navigation arrows on either side of the `Carousel`.
   */
  showArrows?: boolean;
  /**
   * CSS offset value for arrow positioning.  May be helpful with more complex carousel items.
   *
   * Default: `50%`.
   */
  arrowOffset?: string;
  /** The type of pagination to render below the images.
   *
   * @deprecated indicator no longer supported for accessibility reasons. Use `showPaginator`.
   */
  paginatorStyle?: 'indicator' | 'paginator';
  /** Render the paginator below the slides. */
  showPaginator?: boolean;
  /** Make inactive slides slightly transparent */
  fadeInactive?: boolean;
  /**
   * Fade the edges of the `Carousel` to this color. This color is open to any
   * valid CSS string since colors outside of the design system may be used
   * as a background color.
   */
  edgeColor?: string;
  /**
   * Callback called when the active slide changes. Receives the index
   * of the newly active slide, as well as the index of the previously
   * active slide. Additional info about the change is also available,
   * which includes the triggering source of the change.
   */
  onActiveSlideChange?: (
    slideIndex: number,
    previousSlideIndex: number,
    changeInfo: {
      source: ChangeSource;
    }
  ) => void;
  /** The carousel `id`. */
  id?: string;
  /** Color of arrow buttons */
  arrowButtonColor?: LegacyButtonProps['color'];
};

type ArrowButtonProps = {
  isDisabled: boolean;
  direction: 'prev' | 'next';
  yOffset: string;
  onClick: () => void;
  color?: Props['arrowButtonColor'];
};

const ArrowButton = (props: ArrowButtonProps) => {
  const icon = {
    prev: 'arrowSmallLeft',
    next: 'arrowSmallRight',
  }[props.direction] as IconName;

  return (
    <span
      className={styles.arrowButton}
      data-disabled={props.isDisabled ? true : undefined}
      data-direction={props.direction}
      data-zds
    >
      <BaseButton
        aria-label={`${props.direction} image`}
        color={props.color}
        disabled={props.isDisabled}
        isPill={true}
        isSquare={true}
        onClick={props.onClick}
        size="large"
      >
        <Icon isBlock={true} name={icon} size={30} />
      </BaseButton>
    </span>
  );
};

/**
 * <span style="color: #a81227; font-weight: bold;">⚠️ This component is on its way out in favor of [Carousel β](../?path=/docs/beta-carousel--docs).</span>
 *
 * `Carousel` component for rendering a scrollable list of children in a carousel style.
 * Built with the[`ScrollSnap` component](../?path=/docs/display-carousel--docs).
 */
export const Carousel = ({
  children,
  slideWidth,
  arrowOffset = '50%',
  edgeColor = undefined,
  fadeInactive = undefined,
  gap: _deprecatedGapProp = 20,
  id = undefined,
  onActiveSlideChange = undefined,
  paginatorStyle = undefined,
  showArrows = undefined,
  showPaginator = false,
  arrowButtonColor = 'primary',
}: Props) => {
  const gap = 20;

  const [pageIndex, setPageIndex] = useState(0);
  const isFirstPage = pageIndex === 0;
  const isLastPage = pageIndex === Children.count(children) - 1;

  const changePageIndex = (nextIndex: number, source: ChangeSource) => {
    setPageIndex(nextIndex);
    if (onActiveSlideChange && pageIndex !== nextIndex) {
      onActiveSlideChange(nextIndex, pageIndex, { source });
    }
  };

  const generatedPaginatorId = useId();

  const paginatorAriaLabel = id
    ? `${id} carousel paginator`
    : generatedPaginatorId;

  let paginator =
    paginatorStyle &&
    {
      paginator: (
        <Paginator
          aria-label={paginatorAriaLabel}
          currentPageIndex={pageIndex}
          onPaginate={({ pageIndex: index }: OnPaginateData) =>
            changePageIndex(index, 'paginator')
          }
          pageUrls={Array.from({ length: Children.count(children) })}
        />
      ),
      indicator: (
        <Indicator
          aria-label="List of images"
          color="gray"
          count={Children.count(children)}
          noun="image"
          onChangeIndex={(index) => changePageIndex(index, 'indicator')}
          selectedIndex={pageIndex}
        />
      ),
    }[paginatorStyle];

  if (showPaginator || paginatorStyle) {
    paginator = (
      <Paginator
        aria-label={paginatorAriaLabel}
        currentPageIndex={pageIndex}
        onPaginate={({ pageIndex: index }: OnPaginateData) =>
          changePageIndex(index, 'paginator')
        }
        pageUrls={Array.from({ length: Children.count(children) })}
      />
    );
  }

  return (
    <div id={id} data-zds className={styles.carouselWrapper}>
      <div
        className={styles.carousel}
        data-fade-edges={edgeColor || showArrows}
        data-first-page={isFirstPage ? true : undefined}
        data-last-page={isLastPage ? true : undefined}
        style={{
          '--_zds-carousel-edge-color': edgeColor ?? Colors.PrimeWhite,
        }}
        data-zds
      >
        {showArrows && (
          <Fragment>
            <ArrowButton
              direction="prev"
              isDisabled={isFirstPage}
              yOffset={arrowOffset}
              color={arrowButtonColor}
              onClick={() => {
                changePageIndex(pageIndex - 1, 'previous');
              }}
            />
            <ArrowButton
              direction="next"
              isDisabled={isLastPage}
              yOffset={arrowOffset}
              color={arrowButtonColor}
              onClick={() => {
                changePageIndex(pageIndex + 1, 'next');
              }}
            />
          </Fragment>
        )}

        <ScrollSnap
          gap={gap}
          itemWidth={slideWidth}
          onScroll={(index: number) => {
            changePageIndex(index, 'scroll');
          }}
          pageIndex={pageIndex}
          scrollSnapAlign="center"
        >
          {Children.map(children, (child, i) => (
            <div
              className={styles.scrollSnapChild}
              key={i}
              data-faded={fadeInactive && pageIndex !== i}
              data-zds
            >
              {child}
            </div>
          ))}
        </ScrollSnap>
      </div>
      {paginator && (
        <div className={styles.paginator} data-zds>
          {paginator}
        </div>
      )}
    </div>
  );
};
